import { useEffect, useMemo, useState } from "react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Loader2, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import {
  formatMoney,
  getSellingPlans,
  SHOPIFY_HANDLE_BY_SLUG,
} from "@/lib/shopify";

import { productByHandleQueryOptions } from "@/lib/shopify-queries";
import { useCartStore } from "@/stores/cartStore";
import { ShopPayBlock } from "@/components/shop-pay-block";

// Re-export for backward compatibility with existing imports.
export { SHOPIFY_HANDLE_BY_SLUG };

export function ShopifyBuyBox({
  slug,
  handle: handleProp,
  fallbackPrice,
  note,
}: {
  slug?: string;
  /** @deprecated Pass `slug` — alias resolution happens in the loader. */
  handle?: string;
  fallbackPrice: string;
  note?: string;
}) {
  // `handle` is kept for back-compat; prefer the canonical URL slug so the
  // query stays aligned with `productByHandleQueryOptions`'s slug-keyed cache.
  const lookup = slug ?? handleProp;

  if (!lookup) {
    return (
      <div className="mt-8 border border-dashed border-border p-4 text-sm text-muted-foreground">
        This bottle is part of the archive — not currently available for purchase.
      </div>
    );
  }

  return <ShopifyBuyBoxInner slug={lookup} fallbackPrice={fallbackPrice} note={note} />;
}

function ShopifyBuyBoxInner({
  slug,
  fallbackPrice,
  note,
}: {
  slug: string;
  fallbackPrice: string;
  note?: string;
}) {
  const { data: product } = useSuspenseQuery(productByHandleQueryOptions(slug));


  const variants = product?.variants.edges.map((e) => e.node) ?? [];
  const [variantId, setVariantId] = useState<string | undefined>();
  const selected = useMemo(
    () => variants.find((v) => v.id === variantId) ?? variants[0],
    [variants, variantId],
  );

  const addItem = useCartStore((s) => s.addItem);
  const isAdding = useCartStore((s) => s.isLoading);
  const [quantity, setQuantity] = useState(1);

  if (!product || !selected) {
    return (
      <div className="mt-8">
        <p className="font-display text-2xl text-parchment">{fallbackPrice}</p>
        <p className="mt-4 text-sm text-muted-foreground">
          Not currently available — check back soon.
        </p>
      </div>
    );
  }


  const sellingPlans = getSellingPlans(selected);
  const sellingPlanId = sellingPlans[0]?.id ?? null;
  // Stoq pre-order: when a selling plan is attached, treat as pre-order regardless of stock.
  const isPreorder = !!sellingPlanId || (selected.availableForSale && selected.currentlyNotInStock);
  const isBuyable = selected.availableForSale || !!sellingPlanId;
  const isChapterVials = product.handle === "chapter-vials";


  const stepperDisabled = isAdding || !isBuyable;
  const hasTrackedInventory =
    typeof selected.quantityAvailable === "number" && !selected.currentlyNotInStock && !sellingPlanId;
  // Cap the stepper at on-hand inventory. When the merchant hides inventory
  // (quantityAvailable === null) or the variant accepts pre-orders, fall back
  // to 10 so we don't block legitimate sales.
  const maxQty = hasTrackedInventory
    ? Math.max(1, Math.min(10, selected.quantityAvailable as number))
    : 10;

  // When the user switches variants, clamp the current quantity to the new max.
  useEffect(() => {
    setQuantity((q) => Math.min(q, maxQty));
  }, [maxQty]);

  const handleAdd = async () => {
    await addItem({
      variantId: selected.id,
      productHandle: product.handle,
      productTitle: product.title,
      variantTitle: selected.title,
      image: product.featuredImage,
      price: selected.price,
      quantity,
      maxQuantity: hasTrackedInventory ? (selected.quantityAvailable as number) : null,
      selectedOptions: selected.selectedOptions,
      sellingPlanId,
    });
    toast.success(isPreorder ? `Added to cart — ${isChapterVials ? "pre-order" : "reserve"}` : "Added to cart", {
      description:
        selected.title && selected.title !== "Default Title"
          ? `${product.title} — ${selected.title}`
          : product.title,
    });
  };


  return (
    <div className="mt-6">
      <p className="font-display text-2xl text-parchment">
        {formatMoney(selected.price.amount, selected.price.currencyCode)}
      </p>



      {variants.length > 1 && (
        <div className="mt-6">
          <p className="eyebrow mb-3">Size</p>
          <div className="flex flex-wrap gap-2">
            {variants.map((v) => {
              const active = selected.id === v.id;
              const vBuyable = v.availableForSale || getSellingPlans(v).length > 0;
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => setVariantId(v.id)}
                  disabled={!vBuyable}
                  className={`border px-4 py-2 font-display text-xs uppercase tracking-[0.18em] transition-colors ${
                    active
                      ? "border-gold bg-gold/10 text-gold"
                      : "border-border text-foreground/70 hover:border-gold/60 hover:text-gold"
                  } disabled:cursor-not-allowed disabled:opacity-40`}
                >
                  {v.title}
                  {!vBuyable && " · sold out"}
                </button>
              );
            })}
          </div>
        </div>
      )}


      <div className="mt-8">
        <p className="eyebrow mb-3">Quantity</p>
        <div className="inline-flex items-center border border-border">
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            disabled={stepperDisabled || quantity <= 1}
            aria-label="Decrease quantity"
            className="flex h-10 w-10 items-center justify-center text-foreground/70 transition-colors hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Minus className="h-3 w-3" />
          </button>
          <span className="w-10 text-center font-display text-sm text-parchment">
            {quantity}
          </span>
          <button
            type="button"
            onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
            disabled={stepperDisabled || quantity >= maxQty}
            aria-label="Increase quantity"
            className="flex h-10 w-10 items-center justify-center text-foreground/70 transition-colors hover:text-gold disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus className="h-3 w-3" />
          </button>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={handleAdd}
          disabled={isAdding || !isBuyable}
          className="rounded-sm bg-gradient-to-r from-gold-soft to-gold px-8 py-4 font-display text-xs uppercase tracking-[0.24em] text-ink transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isAdding ? (
            <Loader2 className="mx-auto h-4 w-4 animate-spin" />
          ) : !isBuyable ? (
            "Sold out"
          ) : isPreorder ? (
            isChapterVials ? "Pre-order" : "Reserve"
          ) : (
            "Add to cart"
          )}
        </button>
      </div>

      {/* Shop Pay Installments messaging only — the branded button above handles checkout */}
      {isBuyable && !isPreorder && (
        <ShopPayBlock
          variantId={selected.id}
          quantity={quantity}
          price={selected.price.amount}
          currency={selected.price.currencyCode}
          hideButton
        />
      )}


      {note && (
        <p className="mt-4 font-serif text-sm italic text-muted-foreground">{note}</p>
      )}
    </div>
  );
}
